/**
 * @fileoverview Address Search Component with Google Places API v2 (PlaceAutocompleteElement)
 * @module AddressSearch
 *
 * Business Context:
 * - First step in conversion funnel - minimal friction address entry
 * - Validates Texas addresses and extracts county information
 * - Designed to match Ownwell's clean, focused UX
 *
 * Technical Context:
 * - Uses PlaceAutocompleteElement (Places API v2, stable since Feb 2026)
 * - Replaces legacy google.maps.places.Autocomplete (deprecated March 2025)
 * - API loaded via google.maps.importLibrary('places') — no @react-google-maps/api needed
 * - PlaceAutocompleteElement is a web component rendered into a container div
 * - Styled via CSS ::part(input) pseudo-element
 * - Session tokens are managed automatically by the widget
 * - New field naming: addressComponents (camelCase), longText/shortText
 */

import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import { MapPin, Loader2 } from "lucide-react";
import { loadPlacesLibrary } from "../../utils/googleMapsLoader";

export interface AddressData {
  streetAddress: string;
  city: string;
  county: string;
  state: string;
  zip: string;
  fullAddress: string;
}

export interface AddressSearchRef {
  focus: () => void;
  scrollIntoView: () => void;
}

interface AddressSearchProps {
  onAddressSelect: (address: AddressData) => void;
  onError?: (error: string) => void;
  placeholder?: string;
  className?: string;
  buttonText?: string;
  onButtonClick?: () => void;
  disabled?: boolean;
}

/**
 * Extracts structured address fields from Places API v2 addressComponents.
 * New API uses camelCase property names and longText/shortText instead of long_name/short_name.
 */
function extractAddressComponents(
  components: google.maps.places.AddressComponent[],
): Omit<AddressData, "fullAddress"> {
  const result = {
    streetAddress: "",
    city: "",
    county: "",
    state: "",
    zip: "",
  };

  let streetNumber = "";
  let route = "";

  components.forEach((component) => {
    const types = component.types;
    if (types.includes("street_number")) {
      streetNumber = component.longText ?? "";
    } else if (types.includes("route")) {
      route = component.longText ?? "";
    } else if (types.includes("locality")) {
      result.city = component.longText ?? "";
    } else if (types.includes("administrative_area_level_2")) {
      result.county = (component.longText ?? "").replace(/ County$/i, "");
    } else if (types.includes("administrative_area_level_1")) {
      result.state = component.shortText ?? "";
    } else if (types.includes("postal_code")) {
      result.zip = component.longText ?? "";
    }
  });

  result.streetAddress = `${streetNumber} ${route}`.trim();
  return result;
}

export const AddressSearch = forwardRef<AddressSearchRef, AddressSearchProps>(
  (
    {
      onAddressSelect,
      onError,
      placeholder = "Enter your property address",
      className = "",
      buttonText = "Get Savings Estimate",
      onButtonClick,
      disabled = false,
    },
    ref,
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const autocompleteElementRef =
      useRef<google.maps.places.PlaceAutocompleteElement | null>(null);
    const handlePlaceSelectRef = useRef<(event: Event) => Promise<void>>();

    const [inputValue, setInputValue] = useState("");
    const [isLoaded, setIsLoaded] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [error, setError] = useState<string>("");
    const [hasValidAddress, setHasValidAddress] = useState(false);
    const [selectedAddress, setSelectedAddress] = useState<AddressData | null>(
      null,
    );

    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "";

    useImperativeHandle(ref, () => ({
      focus: () => {
        autocompleteElementRef.current?.focus();
      },
      scrollIntoView: () => {
        containerRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        setTimeout(() => {
          autocompleteElementRef.current?.focus();
        }, 500);
      },
    }));

    const handlePlaceSelect = useCallback(
      async (event: Event) => {
        const placeSelectEvent =
          event as google.maps.places.PlaceAutocompletePlaceSelectEvent;
        const place = placeSelectEvent.place;
        if (!place) return;

        try {
          await place.fetchFields({
            fields: ["addressComponents", "formattedAddress"],
          });

          if (!place.addressComponents) {
            const errorMsg = "Please select a valid address from the dropdown";
            setError(errorMsg);
            onError?.(errorMsg);
            setHasValidAddress(false);
            return;
          }

          const components = extractAddressComponents(place.addressComponents);

          if (components.state !== "TX") {
            const errorMsg =
              "Sorry, we only support Texas properties at this time";
            setError(errorMsg);
            onError?.(errorMsg);
            setHasValidAddress(false);
            return;
          }

          if (!components.county) {
            const errorMsg =
              "Could not determine county. Please try a more specific address";
            setError(errorMsg);
            onError?.(errorMsg);
            setHasValidAddress(false);
            return;
          }

          const addressData: AddressData = {
            ...components,
            fullAddress: place.formattedAddress ?? "",
          };

          setError("");
          setHasValidAddress(true);
          setSelectedAddress(addressData);
          setInputValue(place.formattedAddress ?? "");
          onAddressSelect(addressData);
        } catch (err) {
          console.error("Error fetching place details:", err);
          const errorMsg =
            "Failed to retrieve address details. Please try again.";
          setError(errorMsg);
          onError?.(errorMsg);
        }
      },
      [onAddressSelect, onError],
    );

    handlePlaceSelectRef.current = handlePlaceSelect;

    useEffect(() => {
      if (!apiKey) {
        setLoadError(true);
        return;
      }

      let cancelled = false;

      const handlePlaceSelectWrapper = (event: Event) => {
        handlePlaceSelectRef.current?.(event);
      };

      const handleInput = (event: Event) => {
        const input = event.target as HTMLInputElement;
        setInputValue(input.value);
        if (hasValidAddress) {
          setHasValidAddress(false);
          setSelectedAddress(null);
        }
        setError("");
      };

      loadPlacesLibrary(apiKey)
        .then(() => {
          if (cancelled || !wrapperRef.current) return;

          const placeAutocomplete =
            new google.maps.places.PlaceAutocompleteElement({
              componentRestrictions: { country: "us" },
              types: ["address"],
            });

          placeAutocomplete.setAttribute("placeholder", placeholder);
          placeAutocomplete.addEventListener(
            "gmp-select",
            handlePlaceSelectWrapper,
          );
          placeAutocomplete.addEventListener("input", handleInput);

          wrapperRef.current.innerHTML = "";
          wrapperRef.current.appendChild(placeAutocomplete);
          autocompleteElementRef.current = placeAutocomplete;

          if (!cancelled) setIsLoaded(true);
        })
        .catch((err) => {
          console.error("Failed to load Google Maps:", err);
          if (!cancelled) setLoadError(true);
        });

      return () => {
        cancelled = true;
        if (autocompleteElementRef.current) {
          autocompleteElementRef.current.removeEventListener(
            "gmp-select",
            handlePlaceSelectWrapper,
          );
          autocompleteElementRef.current.removeEventListener(
            "input",
            handleInput,
          );
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiKey]);

    const handleButtonClick = () => {
      if (hasValidAddress && selectedAddress) {
        onButtonClick?.();
      } else if (!inputValue) {
        setError("Please enter your property address");
      } else {
        const isTexasAddress =
          inputValue.toLowerCase().includes("tx") ||
          inputValue.toLowerCase().includes("texas");

        if (isTexasAddress) {
          const addressData: AddressData = {
            streetAddress: inputValue.split(",")[0]?.trim() || inputValue,
            city: inputValue.split(",")[1]?.trim() || "",
            county: "Harris",
            state: "TX",
            zip: inputValue.match(/\d{5}/)?.[0] || "",
            fullAddress: inputValue,
          };
          setError("");
          setHasValidAddress(true);
          setSelectedAddress(addressData);
          onAddressSelect(addressData);
        } else {
          setError("Please enter a Texas property address");
        }
      }
    };

    const handleManualEntry = () => {
      if (!inputValue.trim()) {
        setError("Please enter your property address");
        return;
      }

      const isTexasAddress =
        inputValue.toLowerCase().includes("tx") ||
        inputValue.toLowerCase().includes("texas");

      if (!isTexasAddress) {
        setError("Please enter a Texas property address");
        return;
      }

      const addressData: AddressData = {
        streetAddress: inputValue.split(",")[0]?.trim() || inputValue,
        city: inputValue.split(",")[1]?.trim() || "",
        county: "Harris",
        state: "TX",
        zip: "",
        fullAddress: inputValue,
      };

      setError("");
      setHasValidAddress(true);
      setSelectedAddress(addressData);
      onAddressSelect(addressData);
    };

    // Loading state
    if (!isLoaded && !loadError) {
      return (
        <div className={`flex items-center justify-center p-4 ${className}`}>
          <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
          <span className="ml-2 text-gray-500">Loading address search...</span>
        </div>
      );
    }

    // Fallback mode: Google Maps failed or no API key
    if (loadError || !apiKey) {
      return (
        <div className={className}>
          <div className="relative bg-white rounded-2xl shadow-xl border border-gray-200 p-2 md:p-3">
            <div className="flex flex-col md:flex-row gap-2 md:gap-3">
              <div className="relative flex-1">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-teal-500">
                  <MapPin className="w-5 h-5" />
                </div>
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => {
                    setInputValue(e.target.value);
                    if (hasValidAddress) {
                      setHasValidAddress(false);
                      setSelectedAddress(null);
                    }
                    setError("");
                  }}
                  placeholder={placeholder}
                  disabled={disabled}
                  className="w-full px-4 py-4 pl-12 text-lg bg-gray-50 border border-gray-200 rounded-xl
                         focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white
                         disabled:bg-gray-100 disabled:cursor-not-allowed
                         transition-all duration-200"
                  aria-label="Property address"
                  onKeyDown={(e) => e.key === "Enter" && handleManualEntry()}
                />
                {hasValidAddress && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                    <svg
                      className="w-5 h-5 text-green-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                )}
              </div>
              <button
                onClick={handleManualEntry}
                disabled={disabled}
                className="px-8 py-4 text-lg font-semibold rounded-xl transition-all duration-200
                        bg-teal-500 hover:bg-teal-600 text-white shadow-lg hover:shadow-xl
                        transform hover:-translate-y-0.5
                        disabled:bg-gray-300 disabled:cursor-not-allowed
                        whitespace-nowrap"
              >
                {buttonText}
              </button>
            </div>
          </div>
          {error && (
            <p className="mt-3 text-sm text-red-600 text-center" role="alert">
              {error}
            </p>
          )}
          <p className="mt-3 text-sm text-gray-500 text-center">
            Enter your Texas property address (e.g., 123 Main St, Houston, TX
            77001)
          </p>
        </div>
      );
    }

    return (
      <div ref={containerRef} className={className}>
        {/* Search Container */}
        <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-100 p-3 md:p-4">
          <div className="flex flex-col md:flex-row gap-3">
            {/* PlaceAutocompleteElement container */}
            <div className="relative flex-1 min-w-0">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-teal-500 z-10 pointer-events-none">
                <MapPin className="w-5 h-5" />
              </div>
              {/*
              The PlaceAutocompleteElement web component is mounted here.
              Styled via global CSS ::part(input) in index.css.
            */}
              <div ref={wrapperRef} className="gmp-autocomplete-wrapper" />
              {hasValidAddress && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                  <svg
                    className="w-5 h-5 text-green-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              )}
            </div>

            {/* CTA Button */}
            <button
              onClick={handleButtonClick}
              disabled={disabled}
              className="px-8 md:px-10 py-4 text-base md:text-lg font-bold rounded-xl transition-all duration-200
                      bg-teal-500 hover:bg-teal-600 text-white shadow-lg hover:shadow-xl
                      transform hover:-translate-y-0.5 active:translate-y-0
                      disabled:bg-gray-300 disabled:cursor-not-allowed
                      whitespace-nowrap flex-shrink-0"
            >
              {buttonText}
            </button>
          </div>
        </div>

        {error && (
          <p
            className="mt-3 text-sm text-red-600 text-center font-medium"
            role="alert"
          >
            {error}
          </p>
        )}

        <p className="mt-3 text-sm text-gray-400 text-center">
          Enter your Texas property address (e.g., 123 Main St, Houston, TX
          77001)
        </p>
      </div>
    );
  },
);

AddressSearch.displayName = "AddressSearch";

export default AddressSearch;
